import {
  FormInputOptions,
  PaymentType,
} from "checkout-query-client/types/form-types";
import { PaymentLink } from "checkout-query-client/types/payment-link-types";

import { isValueEncrypted } from "encrypt-value/tools";

import {
  AltPaymentMethod,
  ApplePay,
  CardPaymentMethod,
  Cart,
  CreateOrUpdatePaymentLinkRequest,
  EcpPaymentMethod,
  GiftCardPaymentMethod,
  GooglePay,
  LinkStatus,
  Money,
  PayByBankPaymentMethod,
  Paze,
  PlainMessage,
  SepaPaymentMethod,
  SupportedCards,
} from "frontend-client";

export type CardExpirationType = {
  expirationMonth: number | string;
  expirationYear: number | string;
};

export const getExpirationMonthYear = (expirationValue?: string) => {
  if (isValueEncrypted(expirationValue)) {
    const [expirationMonth, expirationYear] = (expirationValue ?? "").split(
      " "
    );
    return { expirationMonth, expirationYear };
  }

  const [expirationMonth, expirationYear] = (expirationValue || "")
    .split(" / ")
    .map(Number);

  return {
    expirationMonth,
    expirationYear: expirationYear + 2000,
  };
};

export type CardMessage = PlainMessage<CardPaymentMethod>;
export type EcpMessage = PlainMessage<EcpPaymentMethod>;
type AppleMessage = PlainMessage<ApplePay>;
type SepaMessage = PlainMessage<SepaPaymentMethod>;
type GoogleMessage = PlainMessage<GooglePay>;
type PazeMessage = PlainMessage<Paze>;
type PayByBankMessage = PlainMessage<PayByBankPaymentMethod>;
type GiftCardMessage = PlainMessage<GiftCardPaymentMethod>;
export type LpmMessage = PlainMessage<
  NonNullable<AltPaymentMethod[keyof PlainMessage<AltPaymentMethod>]>
>;

export const isCreditCardPaymentMethod = (
  paymentMethod:
    | CardMessage
    | EcpMessage
    | AppleMessage
    | SepaMessage
    | PazeMessage
    | PayByBankMessage
    | LpmMessage
    | GiftCardMessage
): paymentMethod is CardMessage => {
  return (paymentMethod as CardMessage)?.securityCodeRequired !== undefined;
};

export const isEcpPaymentMethod = (
  paymentMethod:
    | CardMessage
    | EcpMessage
    | AppleMessage
    | SepaMessage
    | PazeMessage
    | PayByBankMessage
    | LpmMessage
    | GiftCardMessage
): paymentMethod is EcpMessage => {
  return (paymentMethod as EcpMessage)?.confirmAccountNumber !== undefined;
};

type AltPaymentMethods = keyof PlainMessage<AltPaymentMethod>;

const altPaymentMethods: AltPaymentMethods[] = [
  PaymentType.ideal,
  PaymentType.alipay,
  PaymentType.wechatPay,
  PaymentType.trustly,
  PaymentType.paypal,
];

export const isAltPaymentType = (
  paymentType: PaymentType | AltPaymentMethods | undefined
): paymentType is AltPaymentMethods => {
  if (!paymentType) {
    return false;
  }
  return (altPaymentMethods as PaymentType[]).includes(
    paymentType as PaymentType
  );
};

export const getAcceptedMandates = (formFields?: Record<string, string>) => {
  return Object.keys(formFields || {})
    .filter((key) => key.includes("mandate-") && formFields?.[key] === "true")
    .map((id) => id.replace("mandate-", ""));
};

export const hasMopForCurrency = (
  currency: string,
  mop?:
    | CardMessage
    | EcpMessage
    | AppleMessage
    | SepaMessage
    | PazeMessage
    | GoogleMessage
    | PayByBankMessage
    | LpmMessage
): boolean => {
  if (!mop) {
    return true;
  }
  let mopCurrencies = [];
  if (isCreditCardPaymentMethod(mop as CardMessage)) {
    mopCurrencies = (mop as CardMessage).supportedCards.flatMap(
      (card: PlainMessage<SupportedCards>) => card.pointOfSaleCurrencyCodes
    );
  } else if (
    isEcpPaymentMethod(mop as EcpMessage) ||
    (mop as LpmMessage).pointOfSaleCurrencyCodes !== undefined
  ) {
    mopCurrencies = (mop as EcpMessage).pointOfSaleCurrencyCodes;
  } else {
    mopCurrencies = [currency];
  }
  return mopCurrencies.length === 0 || mopCurrencies.includes(currency);
};

const currentYear = new Date().getFullYear();

export const expirationMonths: FormInputOptions[] = Array.from(Array(12)).map(
  (_, index) => {
    let month = (index + 1).toString();

    if (month.length === 1) {
      month = "0" + month;
    }

    return {
      value: month,
      title: month,
    };
  }
);

export const expirationYears: FormInputOptions[] = Array.from(Array(20)).map(
  (_, index) => {
    const year = (currentYear + index).toString();

    return {
      value: year,
      title: year,
    };
  }
);

export const isCardFormFieldValueDifferent = (
  card: CardExpirationType,
  formFieldExpiration: CardExpirationType
) => {
  if (
    formFieldExpiration.expirationMonth !== card.expirationMonth ||
    formFieldExpiration.expirationYear !== card.expirationYear
  ) {
    return true;
  }
  return false;
};

export type DeviceFingerprint = {
  deviceFingerprintSessionId: string;
  timestamp: string;
};

export const getDeviceFingerprintSessionId = () => {
  const storedDeviceFingerprintSessionJson: string | null | undefined =
    localStorage?.getItem("deviceFingerprintSessionId");
  if (storedDeviceFingerprintSessionJson) {
    let parsed: DeviceFingerprint = {
      deviceFingerprintSessionId: "",
      timestamp: "",
    };
    try {
      parsed = storedDeviceFingerprintSessionJson
        ? (JSON.parse(storedDeviceFingerprintSessionJson) as DeviceFingerprint)
        : { deviceFingerprintSessionId: "", timestamp: "" };
    } catch (e) {
      return "";
    }
    return parsed.deviceFingerprintSessionId ?? "";
  }
  return "";
};

// All properties should remain the same except active is being set to false
export const createRequest = (
  link?: PaymentLink
): CreateOrUpdatePaymentLinkRequest => {
  const money = new Money({
    amount: link?.total?.amount ?? BigInt(0),
    decimalCount: link?.total?.decimalCount,
    currencyCode: link?.total?.currencyCode ?? "USD",
  });
  const cart = new Cart({
    totalTransactionAmount: money.amount,
  });

  return new CreateOrUpdatePaymentLinkRequest({
    active: false,
    status: LinkStatus.INACTIVE,
    name: link?.name ?? "",
    cart,
    linkExternalId: link?.externalId ?? "",
  });
};
